import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

// Kept as a plain export, not interpolated into the `w-[128px]` class below -- a Tailwind arbitrary
// value built from a variable produces no rule at all. ScenarioRibbon.jsx imports this for its own
// viewport clamp so the two never drift apart.
export const MENU_WIDTH = 128

const ITEMS = [
  // Letter 104, 1.2: "Edit -> Rename" (the key stays 'edit').
  { key: 'edit', label: 'Rename' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'delete', label: 'Delete' },
]

// 128x94, rx 8, items 26 tall (spec 3.6). Positioned by the caller (ScenarioRibbon.jsx) via `style`,
// in viewport coordinates (position: fixed). Portaled to #portal so it always paints above the
// sticky metrics strip regardless of DOM order -- z-30, between the strip's z-20 and GalleryModal's
// z-50. See README.md ("the scenario menu is portaled").
export default function ScenarioMenu({ style, onEdit, onDuplicate, onDelete, onClose }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose()
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    // capture: true -- scroll events do not bubble, so this is the only way to hear one fired
    // anywhere in the document, including the chip track this menu no longer lives inside (it is
    // portaled to #portal, a document.body sibling of the ribbon's own subtree).
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('scroll', onClose, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('scroll', onClose, true)
    }
  }, [onClose])

  const actions = { edit: onEdit, duplicate: onDuplicate, delete: onDelete }

  return createPortal(
    <ul
      ref={menuRef}
      role="menu"
      aria-label="Scenario actions"
      className="fixed z-30 w-[128px] rounded-[8px] bg-white py-[8px] divide-y divide-[#C8D2C3]"
      style={{ ...style, boxShadow: '0 0 40px rgba(115,147,94,0.15)', outline: '1px solid #C8D2C3', outlineOffset: '-1px' }}
    >
      {ITEMS.map((item) => (
        <li key={item.key}>
          <button
            type="button"
            role="menuitem"
            onClick={() => actions[item.key]()}
            // Delete stays #17270D, not the app's destructive #D85263 -- spec 3.6 is explicit this
            // is a deliberate exception, corroborated by delete-hover's own red trash icon living
            // on a different component entirely (spec 3.10).
            className="w-full text-left px-[16px] h-[26px] text-[12px] text-brand-dark bg-white hover:bg-[#EEF3EC]"
          >
            {item.label}
          </button>
        </li>
      ))}
    </ul>,
    document.getElementById('portal')
  )
}
